import React, { useState } from 'react';
import AMS360CustomerAutocomplete from './AMS360CustomerAutocomplete';
import { ams360Service, AMS360Customer } from '../services/ams360Service';

interface AMS360ImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (customerId: string) => void;
}

const AMS360ImportModal: React.FC<AMS360ImportModalProps> = ({
  isOpen,
  onClose,
  onImport,
}) => {
  const [selectedCustomer, setSelectedCustomer] = useState<AMS360Customer | null>(null);
  const [customerType, setCustomerType] = useState<'customer' | 'prospect' | 'suspect'>('customer');
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSelectCustomer = (customer: AMS360Customer) => {
    setSelectedCustomer(customer);
    setError(null);
  };

  const handleClearSelection = () => {
    setSelectedCustomer(null);
    setError(null);
  };

  const handleImport = async () => {
    if (!selectedCustomer) return;

    setImporting(true);
    setError(null);

    try {
      const result = await ams360Service.importCustomer(selectedCustomer, customerType);

      if (result.success && result.customerId) {
        onImport(result.customerId);
        onClose();
        // Reset state
        setSelectedCustomer(null);
        setCustomerType('customer');
      } else {
        setError(result.error || 'Failed to import customer');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to import customer');
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setSelectedCustomer(null);
    setCustomerType('customer');
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-lg bg-white dark:bg-gray-800 rounded-xl shadow-xl transform transition-all">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Import from AMS360
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Search and import a customer from AMS360
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-4">
            {/* Search / Selected customer */}
            {!selectedCustomer ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Search for a customer
                </label>
                <AMS360CustomerAutocomplete
                  onSelect={handleSelectCustomer}
                  placeholder="Type customer name to search..."
                />
              </div>
            ) : (
              <div className="space-y-4">
                {/* Selected customer card */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Selected customer
                  </label>
                  <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
                          <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                        </div>
                        <div>
                          <div className="font-medium text-gray-900 dark:text-white">
                            {selectedCustomer.customerName}
                          </div>
                          {selectedCustomer.customerNumber && (
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              #{selectedCustomer.customerNumber}
                            </div>
                          )}
                          {selectedCustomer.address && (
                            <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                              {selectedCustomer.address}
                              {selectedCustomer.city && `, ${selectedCustomer.city}`}
                              {selectedCustomer.state && `, ${selectedCustomer.state}`}
                              {selectedCustomer.postalCode && ` ${selectedCustomer.postalCode}`}
                            </div>
                          )}
                          {selectedCustomer.email && (
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {selectedCustomer.email}
                            </div>
                          )}
                          {selectedCustomer.phone && (
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {selectedCustomer.phone}
                            </div>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={handleClearSelection}
                        className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                        title="Clear selection"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Customer type selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Import as
                  </label>
                  <div className="flex gap-2">
                    {(['customer', 'prospect', 'suspect'] as const).map((type) => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => setCustomerType(type)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          customerType === type
                            ? 'bg-green-600 text-white'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                      >
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Error message */}
            {error && (
              <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm">{error}</span>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl">
            <button
              onClick={handleClose}
              disabled={importing}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleImport}
              disabled={!selectedCustomer || importing}
              className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {importing ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Importing...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  Import Customer
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AMS360ImportModal;
